/**
 * The calendar view's arithmetic: which days a view shows, and what each day
 * holds.
 *
 * Days are local: the Worker hands back instants (GET /api/calendar), and
 * this file puts them on the browser's own calendar, so Brussels time and
 * its DST switches never reach the server. Weeks start on Monday.
 *
 * A day holds its meetings and the shares that arrived on it. A share is
 * placed once:
 *   - under a meeting when its link is a record of that meeting (notes, the
 *     deck…) — the meeting already lists it, so the day list skips it;
 *   - under a meeting, dimmer, when someone invited to it shared it that day
 *     and the meeting is small (ATTENDEE_RULE_MAX people or fewer) — the deck
 *     Pieter sent an hour before the 1:1. A larger meeting would claim every
 *     share of the day. With two such meetings, the nearest in time wins;
 *   - otherwise in the day's own list.
 */

import {
  type CalendarEvent,
  type CalendarRange,
  type CalendarShare,
  EVENT_ROLES
} from '@satisa-memory/shared'

export type CalendarViewName = 'month' | 'week' | 'day'
export const VIEWS: CalendarViewName[] = ['month', 'week', 'day']

/** Meetings this size or smaller tie their invitees' shares of the day to them. */
export const ATTENDEE_RULE_MAX = 8

export interface DayMeeting {
  event: CalendarEvent
  /** Shared that day by someone invited, and not a record of the meeting. */
  fromAttendees: CalendarShare[]
}

export interface DayPlan {
  key: string
  allDay: CalendarEvent[]
  meetings: DayMeeting[]
  /** Shares of the day no meeting claims, one per link, in time order. */
  shares: CalendarShare[]
}

export type TimelineItem =
  | { kind: 'meeting'; at: string; meeting: DayMeeting }
  | { kind: 'share'; at: string; share: CalendarShare }

const pad = (n: number) => String(n).padStart(2, '0')

/** The local calendar day of a moment, as YYYY-MM-DD. */
export function dayKey(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** Local midnight of a YYYY-MM-DD key; null when it is not one. */
export function parseDay(key: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key)
  if (!m) return null
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return dayKey(d) === key ? d : null
}

export function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n)
}

function mondayOf(d: Date): Date {
  return addDays(d, -((d.getDay() + 6) % 7))
}

/** The days a view shows around `anchor`, and the window to ask the Worker for. */
export function rangeFor(view: CalendarViewName, anchor: Date) {
  let first: Date
  let count: number
  if (view === 'day') {
    first = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate())
    count = 1
  } else if (view === 'week') {
    first = mondayOf(anchor)
    count = 7
  } else {
    first = mondayOf(new Date(anchor.getFullYear(), anchor.getMonth(), 1))
    const last = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0)
    const end = addDays(mondayOf(last), 7)
    count = Math.round((end.getTime() - first.getTime()) / 86_400_000)
  }
  const days = Array.from({ length: count }, (_, i) => dayKey(addDays(first, i)))
  return { from: first, to: addDays(first, count), days }
}

/** The anchor one period earlier (-1) or later (+1). */
export function step(view: CalendarViewName, anchor: Date, delta: number): Date {
  if (view === 'day') return addDays(anchor, delta)
  if (view === 'week') return addDays(anchor, 7 * delta)
  return new Date(anchor.getFullYear(), anchor.getMonth() + delta, 1)
}

/**
 * The days an event covers. An all-day event is dated, not timed: Calendar
 * gives its dates as UTC midnights, end exclusive, so its days are read from
 * the date part as written. A timed event covers each local day it touches.
 */
export function eventDays(ev: CalendarEvent): string[] {
  const out: string[] = []
  if (ev.allDay) {
    const start = parseDay(ev.startsAt.slice(0, 10))
    const end = ev.endsAt ? parseDay(ev.endsAt.slice(0, 10)) : null
    if (!start) return out
    const stop = end && end > start ? end : addDays(start, 1)
    for (let d = start; d < stop && out.length < 60; d = addDays(d, 1)) out.push(dayKey(d))
    return out
  }
  const start = new Date(ev.startsAt)
  const end = ev.endsAt ? new Date(new Date(ev.endsAt).getTime() - 1) : start
  const last = dayKey(end > start ? end : start)
  for (let d = new Date(start); out.length < 60; d = addDays(d, 1)) {
    const k = dayKey(d)
    out.push(k)
    if (k >= last) break
  }
  return out
}

function invitees(ev: CalendarEvent): Set<string> {
  return new Set(ev.attendees.map((a) => a.personId))
}

function smallMeeting(ev: CalendarEvent): boolean {
  return !ev.allDay && ev.attendeeCount > 0 && ev.attendeeCount <= ATTENDEE_RULE_MAX
}

/** Lay out every day in `keys` from one /api/calendar answer. */
export function planDays(range: CalendarRange, keys: string[]): Map<string, DayPlan> {
  const plans = new Map<string, DayPlan>()
  for (const key of keys) plans.set(key, { key, allDay: [], meetings: [], shares: [] })

  for (const ev of range.events) {
    for (const key of eventDays(ev)) {
      const plan = plans.get(key)
      if (!plan) continue
      if (ev.allDay) plan.allDay.push(ev)
      else plan.meetings.push({ event: ev, fromAttendees: [] })
    }
  }

  const seen = new Set<string>()
  for (const share of [...range.shares].sort((a, b) => a.sharedAt.localeCompare(b.sharedAt))) {
    const key = dayKey(new Date(share.sharedAt))
    const plan = plans.get(key)
    // One line per link per day: the first time it arrived.
    if (!plan || seen.has(`${key}|${share.link.id}`)) continue
    seen.add(`${key}|${share.link.id}`)

    const all = [...plan.meetings.map((m) => m.event), ...plan.allDay]
    if (all.some((ev) => ev.links.some((l) => l.id === share.link.id))) continue

    const by = share.sharedBy?.id
    const at = new Date(share.sharedAt).getTime()
    const claimants = by
      ? plan.meetings.filter((m) => smallMeeting(m.event) && invitees(m.event).has(by))
      : []
    if (claimants.length) {
      const nearest = claimants.reduce((best, m) =>
        Math.abs(new Date(m.event.startsAt).getTime() - at) <
        Math.abs(new Date(best.event.startsAt).getTime() - at)
          ? m
          : best
      )
      nearest.fromAttendees.push(share)
    } else plan.shares.push(share)
  }

  for (const plan of plans.values()) {
    plan.meetings.sort((a, b) => a.event.startsAt.localeCompare(b.event.startsAt))
  }
  return plans
}

/** A day as one list in time order: meetings, and between them what arrived. */
export function timeline(plan: DayPlan): TimelineItem[] {
  const items: TimelineItem[] = [
    ...plan.meetings.map((m) => ({ kind: 'meeting' as const, at: m.event.startsAt, meeting: m })),
    ...plan.shares.map((s) => ({ kind: 'share' as const, at: s.sharedAt, share: s }))
  ]
  return items.sort((a, b) => a.at.localeCompare(b.at))
}

/**
 * The link to open beside a meeting clicked in the week: its record that
 * says most (notes, then transcript, recording, material), else what an
 * invitee shared that day. Null when it has neither.
 */
export function leadLink(m: DayMeeting): string | null {
  const records = [...m.event.links].sort(
    (a, b) => EVENT_ROLES.indexOf(a.role) - EVENT_ROLES.indexOf(b.role)
  )
  return records[0]?.id ?? m.fromAttendees[0]?.link.id ?? null
}

/** A meeting on its day in the calendar, scrolled to. */
export function meetingTo(m: { id: string; startsAt: string }) {
  return {
    name: 'calendar',
    params: { view: 'day', date: dayKey(new Date(m.startsAt)) },
    query: { m: m.id }
  }
}

/** Distinct links a day shows: records, attendee shares and the day's own list. */
export function linkCount(plan: DayPlan): number {
  const ids = new Set<string>()
  for (const m of plan.meetings) {
    // A brief is written for the owner, not a link that reached him.
    for (const l of m.event.links) if (l.kind !== 'brief') ids.add(l.id)
    for (const s of m.fromAttendees) ids.add(s.link.id)
  }
  for (const ev of plan.allDay) for (const l of ev.links) ids.add(l.id)
  for (const s of plan.shares) ids.add(s.link.id)
  return ids.size
}

/** "pieter.meyvaert@teamleader.eu" → "Pieter Meyvaert"; real names pass through. */
export function personLabel(name: string): string {
  if (!name.includes('@')) return name
  return name
    .split('@')[0]!
    .split(/[._-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

export function timeOf(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
}

/** The heading over a view: "October 2026", "12 – 18 Oct 2026", "Tuesday 13 October 2026". */
export function periodTitle(view: CalendarViewName, anchor: Date): string {
  if (view === 'month') {
    return anchor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
  }
  if (view === 'day') {
    return anchor.toLocaleDateString(undefined, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    })
  }
  const { from } = rangeFor('week', anchor)
  const to = addDays(from, 6)
  const sameMonth = from.getMonth() === to.getMonth()
  const head = from.toLocaleDateString(
    undefined,
    sameMonth ? { day: 'numeric' } : { day: 'numeric', month: 'short' }
  )
  const tail = to.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
  return `${head} – ${tail}`
}
